import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AutoAwesomeRounded,
  ClearRounded,
  ReorderRounded,
  SaveOutlined,
} from '@mui/icons-material';
import { Badge, Box, TextField, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';

import { createQuestion, deleteQuestion, updateQuestion } from '../../../../services/dealGPT';
import {
  StyledIconButton,
  StyledTableCell,
  StyledTableRow,
} from '../../../../theme/CustomComponents';
import { Question } from '../../../../Types/dealGPT';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useAuth } from '../../../Providers/AuthProvider';
import { useFeatures } from '../../../Providers/FeatureProvider';
import ConfirmationDialog from '../ConfirmationDialog';
import EditableDialog from '../EditableDialog';
import QuestionLibraryDefaultPrompt from './DefaultPrompt';
import QuestionContextMenu from './QuestionContextMenu';

interface QuestionRowProps {
  editable: boolean;
  reordering?: boolean;
  question: Question;
  refetchQuestions: () => void;
  onNewQuestionSaved: () => void;
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  editable,
  reordering,
  question,
  refetchQuestions,
  onNewQuestionSaved,
}) => {
  const analytics = useAnalytics();
  const features = useFeatures();
  const { isAdmin } = useAuth();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditPromptDialog, setShowEditPromptDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(
    question.id === 'new' ? question : null
  );

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  const onClickSave = useCallback(async () => {
    if (!editingQuestion) return;
    try {
      if (editingQuestion.id === 'new') {
        analytics.event(AnalyticsEvent.WM_IA_QL_ADD_QUESTION);

        await createQuestion({
          question: editingQuestion.question,
          categoryId: editingQuestion.categoryId,
        });
        onNewQuestionSaved();
      } else {
        analytics.event(AnalyticsEvent.WM_IA_QL_EDIT_QUESTION);

        await updateQuestion({
          id: editingQuestion.id,
          question: editingQuestion.question,
        });
      }

      refetchQuestions();
      setEditingQuestion(null);
    } catch {
      enqueueSnackbar('Failed to save question.', { variant: 'error' });
    }
  }, [editingQuestion, refetchQuestions, onNewQuestionSaved, analytics]);

  const saveQuestionPrompt = useCallback(
    async (newPrompt: string) => {
      analytics.event(AnalyticsEvent.WM_IA_QL_QUESTION_CUSTOM_PROMPT);

      await updateQuestion({
        id: question.id,
        customPrompt: newPrompt,
      });
      setShowEditPromptDialog(false);
      refetchQuestions();
    },
    [question, refetchQuestions, analytics]
  );

  const onClickDelete = useCallback(async () => {
    try {
      analytics.event(AnalyticsEvent.WM_IA_QL_DELETE_QUESTION);

      await deleteQuestion(question.id);
      refetchQuestions();
    } catch {
      enqueueSnackbar('Failed to delete question.', { variant: 'error' });
      setShowDeleteConfirmation(false);
    }
  }, [question, refetchQuestions, analytics]);

  return (
    <StyledTableRow key={question.id} ref={setNodeRef} style={style}>
      <StyledTableCell colSpan={editable ? 1 : 2}>
        {editingQuestion ? (
          <TextField
            label={editingQuestion.id === 'new' ? 'New Question' : 'Edit Question'}
            size="small"
            variant="outlined"
            value={editingQuestion?.question}
            onChange={(e) =>
              setEditingQuestion((prev) => prev && { ...prev, question: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onClickSave();
              }
            }}
            fullWidth
            autoFocus
          />
        ) : (
          <Typography>{question.question}</Typography>
        )}
      </StyledTableCell>
      {editable && (
        <StyledTableCell align="right">
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {!editingQuestion && !reordering && (isAdmin || features.ql_custom_prompt) && (
              <Tooltip
                title={
                  question.customPrompt
                    ? 'This question uses a custom prompt, which overrides both the collection-level and default prompts'
                    : 'Define a custom prompt for this question'
                }
                arrow
              >
                <StyledIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditPromptDialog(true);
                  }}
                >
                  <Badge
                    badgeContent={question.customPrompt ? '!' : undefined}
                    color="primary"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    overlap="circular"
                  >
                    <AutoAwesomeRounded />
                  </Badge>
                </StyledIconButton>
              </Tooltip>
            )}
            {editingQuestion && (
              <>
                <Tooltip title="Save" arrow>
                  <StyledIconButton
                    disabled={editingQuestion?.question.length === 0}
                    onClick={onClickSave}
                  >
                    <SaveOutlined />
                  </StyledIconButton>
                </Tooltip>
                <Tooltip title="Cancel" arrow>
                  <StyledIconButton
                    onClick={() => {
                      setEditingQuestion(null);
                      onNewQuestionSaved();
                    }}
                  >
                    <ClearRounded />
                  </StyledIconButton>
                </Tooltip>
              </>
            )}
            {!editingQuestion && !reordering && (
              <QuestionContextMenu
                question={question}
                setEditingQuestion={setEditingQuestion}
                setShowDeleteConfirmation={setShowDeleteConfirmation}
              />
            )}
            {reordering && (
              <Tooltip title="Drag to reorder this question" arrow>
                <StyledIconButton {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
                  <ReorderRounded />
                </StyledIconButton>
              </Tooltip>
            )}
          </Box>
        </StyledTableCell>
      )}
      {showDeleteConfirmation && (
        <ConfirmationDialog
          title="Delete Question?"
          message="Are you sure you want to delete this question? This action cannot be undone."
          close={() => setShowDeleteConfirmation(false)}
          confirm={onClickDelete}
        />
      )}
      {showEditPromptDialog && (
        <EditableDialog
          title="Edit Question Prompt"
          message="You're about to replace the default prompt. Make sure your custom prompt is well-formatted so the system can correctly append the user's question at the end of the prompt"
          initialValue={question.customPrompt || QuestionLibraryDefaultPrompt}
          close={() => setShowEditPromptDialog(false)}
          save={saveQuestionPrompt}
        />
      )}
    </StyledTableRow>
  );
};

export default QuestionRow;
